import type { SystemBanner as SystemBannerData } from '@/features/systemBanner/api/getSystemBanner';
import { useActiveSystemBanners } from '@/features/systemBanner/hooks/useActiveSystemBanners';
import { cn } from '@/utils';
import { cva } from 'class-variance-authority';
import { CircleAlert, Info, TriangleAlert, X } from 'lucide-react';
import { useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getDismissedSystemBannerIds, saveDismissedSystemBannerIds } from './utils/dismissedSystemBanners';
import { SYSTEM_BANNER_HEIGHT_CSS_VAR } from './utils/systemBannerHeight';

const systemBannerVariants = cva('desktop:px-4 fixed top-0 left-0 z-60 flex w-full items-center justify-center gap-3 px-5 py-3', {
  variants: {
    severity: {
      info: 'bg-fill-info-tile-blue text-text-default-primary',
      warning: 'bg-fill-info-tile-orange text-text-default-primary',
      critical: 'bg-fill-status-red text-text-default-inverted',
    },
  },
});

const iconVariants = cva('size-6 shrink-0', {
  variants: {
    severity: {
      info: 'text-outline-icons-blue',
      warning: 'text-outline-icons-orange',
      critical: 'text-text-default-inverted',
    },
  },
});

const severityIcons = {
  info: Info,
  warning: TriangleAlert,
  critical: CircleAlert,
} satisfies Record<SystemBannerData['severity'], unknown>;

const usePublishHeight = (element: HTMLElement | null) => {
  useLayoutEffect(() => {
    const rootStyle = document.documentElement.style;

    if (!element) {
      rootStyle.removeProperty(SYSTEM_BANNER_HEIGHT_CSS_VAR);
      return;
    }

    const publish = () => rootStyle.setProperty(SYSTEM_BANNER_HEIGHT_CSS_VAR, `${element.offsetHeight}px`);
    publish();

    if (typeof ResizeObserver === 'undefined') return () => rootStyle.removeProperty(SYSTEM_BANNER_HEIGHT_CSS_VAR);

    const observer = new ResizeObserver(publish);
    observer.observe(element);

    return () => {
      observer.disconnect();
      rootStyle.removeProperty(SYSTEM_BANNER_HEIGHT_CSS_VAR);
    };
  }, [element]);
};

export const SystemBanner = () => {
  const { t, i18n } = useTranslation('components', { keyPrefix: 'systemBanner' });
  const activeBanners = useActiveSystemBanners();
  const [dismissedIds, setDismissedIds] = useState(getDismissedSystemBannerIds);
  const [element, setElement] = useState<HTMLDivElement | null>(null);

  const banner = activeBanners.find((candidate) => !(candidate.dismissible && dismissedIds.includes(candidate.id)));

  usePublishHeight(banner ? element : null);

  if (!banner) return null;

  const message = (i18n.language === 'en-CA' ? banner.message['en-CA'] : undefined) ?? banner.message.en;
  const Icon = severityIcons[banner.severity];

  const handleDismiss = () => {
    const nextDismissedIds = [...dismissedIds, banner.id];
    setDismissedIds(nextDismissedIds);
    saveDismissedSystemBannerIds(nextDismissedIds);
  };

  return (
    <div
      ref={setElement}
      className={cn(systemBannerVariants({ severity: banner.severity }), banner.dismissible && 'pr-14')}
      style={banner.colors ? { backgroundColor: banner.colors.background, color: banner.colors.text } : undefined}
      role={banner.severity === 'critical' ? 'alert' : 'status'}
      data-testid='system-banner'
    >
      <Icon className={cn(iconVariants({ severity: banner.severity }), banner.colors && 'text-current')} aria-hidden />
      <p className='text-body-m-regular'>{message}</p>
      {banner.dismissible && (
        <button
          type='button'
          onClick={handleDismiss}
          className='rounded-button absolute top-1/2 right-3 flex size-11 -translate-y-1/2 items-center justify-center opacity-80 transition-opacity hover:opacity-100 focus:ring-2 focus:outline-hidden'
        >
          <X className='size-5' aria-hidden />
          <span className='sr-only'>{t('closeButton.accessibleText')}</span>
        </button>
      )}
    </div>
  );
};
